/* Zaroori Jini OMS — Public Pricing Page
 * Rich enterprise pricing page with plans, credit explanation, comparison table,
 * ROI Calculator, add-on credit packs, and FAQ.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, XCircle, ArrowRight, Package, Sparkles,
  ShieldCheck, HeadphonesIcon, Zap, ChevronDown,
} from "lucide-react";
import {
  RED, RED_DARK, INK, MUTED, CREAM, BORDER,
  Chip, Section, SectionTitle, PrimaryButton, GhostButton,
  MarketingShell,
} from "@/components/marketing/atoms";
import ROICalculator, { PLANS } from "@/components/marketing/ROICalculator";

/* ============================ HERO ============================ */
function PricingHero() {
  return (
    <section
      style={{
        background: `linear-gradient(180deg, ${CREAM} 0%, #fff 100%)`,
        padding: "72px 24px 40px", borderBottom: `1px solid ${BORDER}`,
      }}
      data-testid="pricing-hero"
    >
      <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
        <Chip>Simple · Transparent · Credit-Based</Chip>
        <h1
          style={{
            fontSize: "clamp(40px, 5.4vw, 64px)", fontWeight: 800, color: INK,
            marginTop: 20, letterSpacing: "-0.035em", lineHeight: 1.05,
          }}
        >
          Pay for what you process.<br />
          <span style={{ color: RED }}>Nothing more.</span>
        </h1>
        <p
          style={{
            color: MUTED, fontSize: 17, lineHeight: 1.65,
            maxWidth: 640, margin: "22px auto 0",
          }}
        >
          1 Credit = 1 Order Processed. Every plan includes all modules, unlimited users,
          unlimited warehouses, and every marketplace, courier & ERP integration.
        </p>
      </div>
    </section>
  );
}

/* ============================ PLAN CARDS ============================ */
function PlanCards() {
  return (
    <Section id="plans" style={{ background: "#fff", paddingTop: 56 }}>
      <div
        className="pricing-plan-grid"
        style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20 }}
      >
        {PLANS.map((p) => {
          const popular = p.popular;
          const isEnterprise = p.key === "enterprise";
          return (
            <div
              key={p.key}
              data-testid={`pricing-card-${p.key}`}
              style={{
                position: "relative", background: popular ? "#0F172A" : "#fff",
                color: popular ? "#fff" : INK,
                border: popular ? `1.5px solid ${RED}` : `1px solid ${BORDER}`,
                borderRadius: 16, padding: "30px 24px",
                boxShadow: popular ? "0 30px 70px -20px rgba(220,38,38,0.4)" : "0 8px 30px -20px rgba(15,23,42,0.1)",
                transform: popular ? "translateY(-8px)" : "none",
                transition: "transform 200ms ease",
              }}
            >
              {popular && (
                <div
                  style={{
                    position: "absolute", top: -13, left: "50%", transform: "translateX(-50%)",
                    background: RED, color: "#fff", padding: "5px 14px", borderRadius: 999,
                    fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  ⭐ Most Popular
                </div>
              )}
              <div
                style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: popular ? "#F87171" : RED,
                }}
              >
                {p.ideal}
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, marginTop: 8, color: popular ? "#fff" : INK }}>
                {p.name}
              </div>
              <div style={{ marginTop: 16 }}>
                <span
                  style={{
                    fontSize: 40, fontWeight: 800,
                    letterSpacing: "-0.03em",
                    color: popular ? "#fff" : INK,
                  }}
                >
                  ₹{p.price.toLocaleString("en-IN")}
                </span>
                <div style={{ fontSize: 12, color: popular ? "#CBD5E1" : MUTED, marginTop: 2 }}>
                  Valid for {p.validityMonths} month{p.validityMonths > 1 ? "s" : ""}
                </div>
              </div>

              <div
                style={{
                  marginTop: 20, padding: "14px 12px",
                  background: popular ? "rgba(255,255,255,0.06)" : CREAM,
                  border: popular ? "1px solid rgba(255,255,255,0.1)" : `1px solid ${BORDER}`,
                  borderRadius: 10,
                }}
              >
                <Row popular={popular} label="Order cost" value={`₹${p.costPerOrder}`} />
                <Row popular={popular} label="Credits included" value={p.credits.toLocaleString("en-IN")} />
                <Row popular={popular} label="Per credit" value={`₹${p.costPerOrder}`} />
              </div>

              <div style={{ marginTop: 22 }}>
                {isEnterprise ? (
                  <PrimaryButton as="link" to="/contact-sales" full testid={`plan-cta-${p.key}`}>
                    Talk to Sales
                  </PrimaryButton>
                ) : (
                  <PrimaryButton as="link" to="/login" full testid={`plan-cta-${p.key}`}>
                    Get Started
                  </PrimaryButton>
                )}
                <div style={{ marginTop: 10, textAlign: "center" }}>
                  <Link
                    to="/demo"
                    style={{
                      fontSize: 12, fontWeight: 700,
                      color: popular ? "#F87171" : RED, textDecoration: "none",
                    }}
                    data-testid={`plan-demo-${p.key}`}
                  >
                    Book Demo →
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ textAlign: "center", marginTop: 26, fontSize: 13, color: MUTED }}>
        All prices exclude applicable GST. Credits carry forward within plan validity.
      </div>
      <style>{`
        @media (max-width: 1080px) { .pricing-plan-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 560px)  { .pricing-plan-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </Section>
  );
}
function Row({ popular, label, value }) {
  return (
    <div
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "3px 0",
      }}
    >
      <span style={{ fontSize: 12, color: popular ? "#CBD5E1" : MUTED }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: popular ? "#fff" : INK }}>{value}</span>
    </div>
  );
}

/* ============================ CREDIT EXPLANATION ============================ */
function CreditExplanation() {
  return (
    <Section style={{ background: CREAM }} id="credits">
      <SectionTitle
        eyebrow="How Credits Work"
        title="One Simple Unit. All Modules Included."
        description="No hidden per-user, per-integration, or per-marketplace fees. Just credits."
      />
      <div
        className="credits-grid"
        style={{
          display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18,
        }}
      >
        {[
          { icon: <Package />,    step: "01", title: "Choose a Plan",   desc: "Pick the plan that matches your order volume." },
          { icon: <Zap />,        step: "02", title: "Get Credits",     desc: "Credits are loaded into your account instantly." },
          { icon: <Sparkles />,   step: "03", title: "Process Orders",  desc: "Each order processed = 1 credit consumed." },
          { icon: <ShieldCheck />,step: "04", title: "Top Up Anytime",  desc: "Never run out — add-on packs are available on demand." },
        ].map((c) => (
          <div
            key={c.step}
            style={{
              background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12,
              padding: 22,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div
                style={{
                  width: 34, height: 34, borderRadius: 8,
                  background: "#FFF5F5", color: RED,
                  display: "grid", placeItems: "center",
                }}
              >
                {c.icon}
              </div>
              <span
                style={{
                  fontSize: 11, fontWeight: 800, color: MUTED,
                  letterSpacing: "0.14em",
                }}
              >
                STEP {c.step}
              </span>
            </div>
            <div style={{ fontWeight: 800, fontSize: 15, color: INK }}>{c.title}</div>
            <div style={{ fontSize: 13, color: MUTED, marginTop: 6, lineHeight: 1.55 }}>{c.desc}</div>
          </div>
        ))}
      </div>
      <style>{`
        @media (max-width: 900px) { .credits-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 520px) { .credits-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </Section>
  );
}

/* ============================ INCLUDED FEATURES ============================ */
const INCLUDED = [
  { title: "Order Management",        items: ["Unlimited order processing", "Bulk actions", "Split/Merge orders", "Status tracking"] },
  { title: "Inventory",               items: ["Real-time sync", "Multi-warehouse", "Low stock alerts", "Reservations"] },
  { title: "Shipping",                items: ["AWB generation", "Courier allocation", "Labels & tracking", "COD reconciliation"] },
  { title: "Marketplace Integrations",items: ["Amazon", "Flipkart", "Myntra", "AJIO", "Nykaa", "Tata CliQ", "Meesho", "Shopify", "WooCommerce"] },
  { title: "AI & Automation",         items: ["AI insights", "Exception detection", "Inventory recommendations", "Workflow automation"] },
  { title: "Reports",                 items: ["Sales dashboard", "Inventory reports", "Marketplace analytics", "Exports"] },
  { title: "Support",                 items: ["Email support", "Training", "Knowledge base", "Community"] },
];

function IncludedFeatures() {
  return (
    <Section style={{ background: "#fff" }}>
      <SectionTitle
        eyebrow="Every Credit Includes"
        title="All Modules. All Integrations. Zero Add-Ons."
      />
      <div
        className="included-grid"
        style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18 }}
      >
        {INCLUDED.map((g) => (
          <div
            key={g.title}
            data-testid={`included-${g.title.toLowerCase().replace(/\s+/g, "-")}`}
            style={{
              background: CREAM, border: `1px solid ${BORDER}`, borderRadius: 12,
              padding: 22,
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 14, color: INK, marginBottom: 12 }}>
              {g.title}
            </div>
            <ul style={{ padding: 0, listStyle: "none" }}>
              {g.items.map((it) => (
                <li
                  key={it}
                  style={{
                    fontSize: 12, color: INK, padding: "4px 0",
                    display: "flex", alignItems: "center", gap: 6,
                  }}
                >
                  <CheckCircle2 size={12} color={RED} /> {it}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <style>{`
        @media (max-width: 1080px) { .included-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 520px)  { .included-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </Section>
  );
}

/* ============================ ADD-ON PACKS ============================ */
const ADDON_PACKS = [
  { credits: "5,000",   price: "₹5,000",  perOrder: "₹1.00" },
  { credits: "10,000",  price: "₹9,000",  perOrder: "₹0.90" },
  { credits: "25,000",  price: "₹20,000", perOrder: "₹0.80" },
  { credits: "50,000+", price: "Custom",  perOrder: "Talk to Sales" },
];

function AddonPacks() {
  return (
    <Section id="addons" style={{ background: CREAM }}>
      <SectionTitle
        eyebrow="Add-On Credit Packs"
        title="Top Up Anytime. Volume Discounts Built In."
      />
      <div
        className="addon-grid"
        style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18 }}
      >
        {ADDON_PACKS.map((a) => (
          <div
            key={a.credits}
            data-testid={`addon-pack-${a.credits.replace(/[+,]/g, "")}`}
            style={{
              background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12,
              padding: 22, textAlign: "center",
            }}
          >
            <div style={{ fontSize: 12, color: RED, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase" }}>
              Credits
            </div>
            <div style={{ fontSize: 30, fontWeight: 800, color: INK, marginTop: 6, letterSpacing: "-0.02em" }}>
              {a.credits}
            </div>
            <div style={{ height: 1, background: BORDER, margin: "16px 0" }} />
            <div style={{ fontSize: 22, fontWeight: 800, color: INK }}>{a.price}</div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>{a.perOrder} / order</div>
            <div style={{ marginTop: 18 }}>
              <PrimaryButton
                as="link"
                to={a.price === "Custom" ? "/contact-sales" : "/login"}
                full
                testid={`addon-cta-${a.credits.replace(/[+,]/g, "")}`}
              >
                {a.price === "Custom" ? "Talk to Sales" : "Buy Credits"}
              </PrimaryButton>
            </div>
          </div>
        ))}
      </div>
      <style>{`
        @media (max-width: 900px) { .addon-grid { grid-template-columns: repeat(2, 1fr) !important; } }
      `}</style>
    </Section>
  );
}

/* ============================ COMPARISON TABLE ============================ */
const COMP_ROWS = [
  { feat: "Order Management",              launch: true, growth: true, scale: true, enterprise: true },
  { feat: "Real-time Inventory Sync",      launch: true, growth: true, scale: true, enterprise: true },
  { feat: "Warehouse Management (WMS)",    launch: true, growth: true, scale: true, enterprise: true },
  { feat: "All Marketplace Integrations",  launch: true, growth: true, scale: true, enterprise: true },
  { feat: "Courier Integrations (20+)",    launch: true, growth: true, scale: true, enterprise: true },
  { feat: "AI Command Center",             launch: true, growth: true, scale: true, enterprise: true },
  { feat: "Automation Engine",             launch: "Basic", growth: "Advanced", scale: "Advanced", enterprise: "Advanced + Visual" },
  { feat: "Unlimited Users",               launch: true, growth: true, scale: true, enterprise: true },
  { feat: "Unlimited Warehouses",          launch: true, growth: true, scale: true, enterprise: true },
  { feat: "API & Webhooks",                launch: true, growth: true, scale: true, enterprise: true },
  { feat: "Priority Support",              launch: false, growth: false, scale: true, enterprise: true },
  { feat: "Dedicated Onboarding",          launch: false, growth: false, scale: true, enterprise: true },
  { feat: "SSO (SAML/OIDC)",               launch: false, growth: false, scale: false, enterprise: true },
  { feat: "White-Label Branding",          launch: false, growth: false, scale: false, enterprise: true },
  { feat: "Custom SLA",                    launch: false, growth: false, scale: false, enterprise: true },
];

function ComparisonTable() {
  return (
    <Section id="compare" style={{ background: "#fff" }}>
      <SectionTitle
        eyebrow="Compare Plans"
        title="What's Different Across Plans"
      />
      <div style={{ overflowX: "auto" }} data-testid="pricing-compare-table">
        <table
          style={{
            width: "100%", minWidth: 720, borderCollapse: "separate",
            borderSpacing: 0, background: "#fff",
            border: `1px solid ${BORDER}`, borderRadius: 12, overflow: "hidden",
          }}
        >
          <thead>
            <tr style={{ background: CREAM }}>
              <th style={cellH}>Feature</th>
              {PLANS.map((p) => (
                <th
                  key={p.key}
                  style={{
                    ...cellH, textAlign: "center",
                    background: p.popular ? "#0F172A" : CREAM,
                    color: p.popular ? "#fff" : INK,
                  }}
                >
                  {p.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMP_ROWS.map((r, i) => (
              <tr key={r.feat} style={{ background: i % 2 === 0 ? "#fff" : CREAM }}>
                <td style={{ ...cell, fontWeight: 600 }}>{r.feat}</td>
                {["launch", "growth", "scale", "enterprise"].map((k) => (
                  <td key={k} style={{ ...cell, textAlign: "center" }}>
                    {r[k] === true ? (
                      <CheckCircle2 size={18} color={RED} style={{ display: "inline-block" }} />
                    ) : r[k] === false ? (
                      <XCircle size={18} color="#CBD5E1" style={{ display: "inline-block" }} />
                    ) : (
                      <span style={{ fontSize: 13, fontWeight: 600, color: INK }}>{r[k]}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
const cellH = { padding: "14px 16px", textAlign: "left", fontSize: 13, fontWeight: 700, color: INK, borderBottom: `1px solid ${BORDER}` };
const cell = { padding: "14px 16px", fontSize: 13, color: INK, borderBottom: `1px solid ${BORDER}` };

/* ============================ ROI ============================ */
function ROISection() {
  return (
    <Section id="roi" style={{ background: CREAM }}>
      <SectionTitle
        eyebrow="ROI Calculator"
        title="Estimate Your Monthly Savings in 10 Seconds"
      />
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <ROICalculator />
      </div>
    </Section>
  );
}

/* ============================ PRICING FAQ ============================ */
const P_FAQS = [
  { q: "Do credits expire?", a: "Credits are valid for the duration of your plan. Unused credits carry forward within plan validity. Enterprise plans include auto-renewal with credit rollover options." },
  { q: "What counts as 1 credit?", a: "1 credit = 1 order processed (any source: marketplace, D2C, POS, B2B). Cancellations before dispatch don't consume credits. Failed marketplace orders that never enter the pipeline are free." },
  { q: "Can I upgrade or downgrade?", a: "Yes. Upgrade anytime — remaining credits carry over and new credits are prorated. Downgrades take effect at the end of the current cycle." },
  { q: "Do you offer volume discounts?", a: "Yes. Add-on packs of 25,000+ credits already have per-credit discounts. For 50,000+ or annual commits, contact sales for custom pricing." },
  { q: "Is GST included?", a: "All listed prices exclude applicable GST. GST is added at checkout as per Indian tax regulations." },
  { q: "Do you offer refunds?", a: "Credits are non-refundable once consumed. Unused credits within the current billing cycle can be refunded on written request within 7 days of purchase." },
];

function PricingFAQ() {
  return (
    <Section id="pricing-faq" style={{ background: "#fff" }}>
      <SectionTitle eyebrow="Pricing FAQ" title="Common Pricing Questions" />
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        {P_FAQS.map((f, i) => <FAQItem key={f.q} q={f.q} a={f.a} testid={`pricing-faq-${i}`} />)}
      </div>
    </Section>
  );
}

function FAQItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${BORDER}`, padding: "18px 0" }} data-testid={testid}>
      <button
        type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        style={{
          background: "none", border: "none", cursor: "pointer", padding: 0,
          width: "100%", display: "flex", justifyContent: "space-between",
          alignItems: "center", textAlign: "left", color: INK,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 16 }}>{q}</span>
        <ChevronDown
          size={20} color={MUTED}
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 200ms ease" }}
        />
      </button>
      {open && (
        <div style={{ marginTop: 12, fontSize: 14, color: MUTED, lineHeight: 1.7 }}>{a}</div>
      )}
    </div>
  );
}

/* ============================ FINAL CTA ============================ */
function PricingCTA() {
  return (
    <Section
      style={{
        background: `linear-gradient(135deg, ${RED} 0%, ${RED_DARK} 100%)`,
        color: "#fff", padding: "80px 24px",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 800, letterSpacing: "-0.02em" }}>
          Still evaluating? Talk to our team.
        </h2>
        <p style={{ maxWidth: 640, margin: "16px auto 0", fontSize: 16, opacity: 0.92, lineHeight: 1.6 }}>
          We&apos;ll set up a personalized demo, migrate your data, and configure your first
          marketplace within 48 hours.
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 30, flexWrap: "wrap" }}>
          <PrimaryButton as="link" to="/demo" testid="pricing-cta-demo">
            Book a Demo <ArrowRight size={16} />
          </PrimaryButton>
          <a
            href="/contact-sales"
            data-testid="pricing-cta-contact"
            style={{
              padding: "13px 26px", background: "transparent", color: "#fff",
              borderRadius: 8, border: "1.5px solid rgba(255,255,255,0.6)",
              fontWeight: 700, fontSize: 14, textDecoration: "none",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
          >
            Contact Sales
          </a>
        </div>
      </div>
    </Section>
  );
}

/* ============================ ROOT ============================ */
export default function Pricing() {
  return (
    <MarketingShell testid="pricing-page">
      <PricingHero />
      <PlanCards />
      <CreditExplanation />
      <IncludedFeatures />
      <AddonPacks />
      <ComparisonTable />
      <ROISection />
      <PricingFAQ />
      <PricingCTA />
    </MarketingShell>
  );
}
